import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, NavigationStart } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { BodyService, EventService, ScApiService, ScrollbarFadeService } from '@sc-ui';
import { Location } from '@angular/common';
import { PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { DemoService } from 'src/services/demo.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  title = 'skillconomy';

  private lastPoppedUrl: string;
  private yScrollStack: number[] = [];
  private isProcessingAccessCode = false;
  private handledAccessCode: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private bodyService: BodyService,
    private location: Location,
    private api: ScApiService,
    private demo: DemoService,
    private scrollbarFade: ScrollbarFadeService,
    @Inject(PLATFORM_ID) private platformId: string) {

  }

  private navigateToLogin() {

  }

  ngOnInit(): void {
    this.scrollbarFade.enable();

    // on NavigationStart, so the views of that navigation already load in the right mode
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => this.demo.applyQueryParam(this.router.parseUrl(event.url).queryParams['demo']));

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(map(() => this.route))
      .pipe(map(route => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }))
      .pipe(filter(route => route.outlet === 'primary'))
      .pipe(mergeMap(route => route.data))
      .subscribe(event => this.handleRouteData(event));


    this.location.subscribe((ev: any) => {
      console.log(ev);
      this.lastPoppedUrl = ev.url;
    });

    this.route.queryParams.subscribe(params => {      

      if (params.access_code) {

        const urlTree = this.router.parseUrl(this.router.url);

        ['u', 'eMail', 't', 's', 'target', 'access_code'].forEach(k => {
          delete urlTree.queryParams[k];
        });

        const target = params.target ?? this.router.serializeUrl(urlTree);
        this.handleAccessCodeLogin(params.access_code, target);
      }
    });

    this.router.events.subscribe((ev: any) => {

      if (isPlatformServer(this.platformId)) return;

      if (ev instanceof NavigationStart) {
        if (ev.url != this.lastPoppedUrl)
          this.yScrollStack.push(window.scrollY);
      } else if (ev instanceof NavigationEnd) {
        if (ev.url == this.lastPoppedUrl) {
          this.lastPoppedUrl = undefined;
          var scrollY = this.yScrollStack.pop();
          setTimeout(() => window.scrollTo(0, scrollY), 500);
        } else {
          window.scrollTo(0, 0);
        }
      }
    });
  }

  handleRouteData(data: any) {
    if (!data) return;
    var title = data.title ? this.title + ' - ' + data.title : this.title;
    this.titleService.setTitle(title);

    if (!data.bodyClasses) {
      this.bodyService.clearClasses();
      return;
    }

    data.bodyClasses.forEach(c => this.bodyService.addClass(c));
  }

  removeURLParameter(url, parameter) {
    //prefer to use l.search if you have a location/link object
    var urlparts = url.split('?');
    if (urlparts.length >= 2) {

      var prefix = encodeURIComponent(parameter) + '=';
      var pars = urlparts[1].split(/[&;]/g);

      //reverse iteration as may be destructive
      for (var i = pars.length; i-- > 0;) {
        //idiom for string.startsWith
        if (pars[i].lastIndexOf(prefix, 0) !== -1) {
          pars.splice(i, 1);
        }
      }

      return urlparts[0] + (pars.length > 0 ? '?' + pars.join('&') : '');
    }
    return url;
  }

  private handleAccessCodeLogin(accessCode: string, target?: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!accessCode) return;
    if (this.isProcessingAccessCode) return;
    if (this.handledAccessCode === accessCode && this.api.isLoggedIn) return;

    this.isProcessingAccessCode = true;

    this.api.loginWithCode(accessCode).subscribe({
      next: (response: any) => {

        this.handledAccessCode = accessCode;
        this.clearAccessCodeQueryParams();

        if (response?.Succeeded) {
          const nextUrl = this.removeURLParameter(target || '/dashboard', 'access_code');
          this.router.navigateByUrl(nextUrl);
        }
        else {
          if (response.RegistrationSecret) {
            this.router.navigate(['/register', response.RegistrationSecret], {
              queryParams: target ? { target: target } : {}
            });
          }
        }
      },
      error: (error) => {
        console.error('Access code login failed.', error);
        this.clearAccessCodeQueryParams();
      },
      complete: () => {
        this.isProcessingAccessCode = false;
      }
    });
  }

  private clearAccessCodeQueryParams() {
    const current = this.router.parseUrl(this.router.url);
    delete current.queryParams.access_code;
    void this.router.navigateByUrl(current, { replaceUrl: true });
  }
}
